<template>
  <div>
    <div class="datepicker__tooltip" v-if="showTooltip && options.hoveringTooltip" v-html="tooltipMessageDisplay"></div>
    <div class="datepicker__month-day" @click.prevent.stop="dayClicked(date)"
      @keyup.enter.prevent.stop="dayClicked(date)" :class="dayClass" :style="isToday ? currentDateStyle : ''"
      :tabindex="tabIndex" ref="day">
      {{ dayNumber }}
    </div>
  </div>
</template>

<script setup lang="ts">
import fecha from 'fecha'

function addDays(date: Date, quantity: number) {
  let result = new Date(date);
  result.setDate(result.getDate() + quantity);
  return result;
}

function countDays(start: Date, end: Date) {
  const oneDay = 24 * 60 * 60 * 1000;
  const firstDate = new Date(start);
  const secondDate = new Date(end);

  return Math.round(Math.abs((firstDate.getTime() - secondDate.getTime()) / (oneDay)));
}

function isDateLessOrEquals(time1: Date, time2: Date) {
  return new Date(time1) <= new Date(time2);
}
// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  sortedDisabledDates: {
    type: Array as PropType<Date[]>
  },
  options: {
    type: Object,
    required: true
  },
  checkIn: {
    type: Date
  },
  checkOut: {
    type: Date
  },
  hoveringDate: {
    type: Date
  },
  belongsToThisMonth: {
    type: Boolean
  },
  activeMonthIndex: {
    type: Number
  },
  date: {
    type: Date,
    required: true
  },
  dayNumber: {
    type: String
  },
  nextDisabledDate: {
    type: [Date, Number, String]
  },
  tooltipMessage: {
    default: null,
    type: String
  },
  currentDateStyle: {
    required: true,
  }
})

// Emits
const emit = defineEmits(['day-clicked'])

// Refs
const day = ref(null)
const isHighlighted = ref(false)
const isDisabled = ref(false)
const allowedCheckoutDays = ref<Date[]>([])
const currentDate = ref(new Date())

function compareDay(day1: Date, day2: Date) {
  const date1 = fecha.format(new Date(day1), 'YYYYMMDD');
  const date2 = fecha.format(new Date(day2), 'YYYYMMDD');

  if (date1 > date2) { return 1; }

  else if (date1 == date2) { return 0; }

  else if (date1 < date2) { return -1; }
}

// Computed
const tabIndex = computed(() => {
  if (!props.isOpen || !props.belongsToThisMonth || isDisabled.value || !isClickable()) {
    return -1
  }
  return 0
})

const nightsCount = computed(() =>
  props.checkIn && props.hoveringDate ? countDays(props.checkIn, props.hoveringDate) : 0
)

const tooltipMessageDisplay = computed(() =>
  props.tooltipMessage
    ? props.tooltipMessage
    : `${nightsCount.value} ${nightsCount.value !== 1
      ? props.options.i18n['nights']
      : props.options.i18n['night']}`
)

const showTooltip = computed(() =>
  !isDisabled.value &&
  props.date == props.hoveringDate &&
  props.checkIn !== null &&
  props.checkOut == null
)

const isToday = computed(() =>
  compareDay(currentDate.value, props.date) == 0
)

const dayClass = computed(() => {
  if (props.belongsToThisMonth) {
    // If the calendar has a minimum number of nights
    if (!isDisabled.value &&
      props.checkIn && compareDay(props.date, props.checkIn) == 1 &&
      props.options.minNights > 0 &&
      props.checkIn && compareDay(
        props.date,
        addDays(props.checkIn, props.options.minNights)
      ) == -1) {
      return 'datepicker__month-day--selected datepicker__month-day--out-of-range'
    }

    // If the calendar has allowed ranges
    if (props.options.allowedRanges.length !== 0) {
      if (!isDisabled.value && props.checkIn !== null && props.checkOut == null) {
        // If the day is one of the allowed check out days and is not highlighted
        if (allowedCheckoutDays.value.some((i) => compareDay(i, props.date) == 0 && !isHighlighted.value)) {
          return 'datepicker__month-day--allowed-checkout'
        }
        // If the day is one of the allowed check out days and is highlighted
        if (allowedCheckoutDays.value.some((i) => compareDay(i, props.date) == 0 && isHighlighted.value)) {
          return 'datepicker__month-day--selected datepicker__month-day--allowed-checkout'
        }
        // If the day is not one of the allowed Checkout Days and is highlighted
        if (!(allowedCheckoutDays.value.some((i) => compareDay(i, props.date) == 0)) && isHighlighted.value) {
          return 'datepicker__month-day--out-of-range datepicker__month-day--selected'
        }
        else {
          return 'datepicker__month-day--out-of-range'
        }
      }
    }
    // Highlight the selected dates and prevent the user from selecting
    // the same date for checkout and checkin
    if (props.checkIn !== null &&
      props.checkIn && fecha.format(props.checkIn, 'YYYYMMDD') == fecha.format(props.date, 'YYYYMMDD')
    ) {
      if (props.options.minNights == 0) {
        return "datepicker__month-day--first-day-selected"
      } else {
        return "datepicker__month-day--disabled datepicker__month-day--first-day-selected"
      }
    }
    if (props.checkOut !== null) {
      if (props.checkOut && fecha.format(props.checkOut, 'YYYYMMDD') == fecha.format(props.date, 'YYYYMMDD')) {
        return "datepicker__month-day--disabled datepicker__month-day--last-day-selected"
      }
    }
    // Only highlight dates that are not disabled
    if (isHighlighted.value && !isDisabled.value) { return " datepicker__month-day--selected" }
    if (isDisabled.value) { return "datepicker__month-day--disabled" }
  }

  else if (!props.belongsToThisMonth) { return "datepicker__month-day--hidden" }
  else { return "datepicker__month-day--valid" }
})

// Methods
const isClickable = () => {
  if (day.value) {
    return getComputedStyle(day.value).pointerEvents !== 'none'
  }
  return true
}

const dayClicked = (date: Date) => {
  if (isDisabled.value || !isClickable()) return

  if (props.options.allowedRanges.length !== 0) {
    createAllowedCheckoutDays(date)
  }

  const nextDisabledDate =
    (props.options.maxNights ? addDays(props.date, props.options.maxNights) : null) ||
    allowedCheckoutDays.value[allowedCheckoutDays.value.length - 1] ||
    Infinity

  emit('day-clicked', { date, nextDisabledDate })
}

const compareEndDay = () => {
  if (props.options.endDate !== Infinity) {
    return (compareDay(props.date, props.options.endDate) == 1)
  }
}

const checkIfDisabled = () => {
  isDisabled.value =
    // If this day is equal any of the disabled dates
    (props.sortedDisabledDates ? props.sortedDisabledDates.some((i) =>
      compareDay(i, props.date) == 0
    ) : null)
    // Or is before the start date
    || compareDay(props.date, props.options.startDate) == -1
    // Or is after the end date
    || compareEndDay()
    // Or is in one of the disabled days of the week
    || props.options.disabledDaysOfWeek.some((i) =>
      i == fecha.format(props.date, 'dddd')
    );
  // Handle checkout enabled
  if (props.options.enableCheckout) {
    if (props.checkIn && compareDay(props.date, props.checkIn) == 1 &&
      props.checkOut && compareDay(props.date, props.checkOut) == -1) {
      isDisabled.value = false;
    }
  }
}

const checkIfHighlighted = () => {
  if (props.checkIn !== null && props.checkOut !== null && isDisabled.value == false) {
    props.checkIn && props.checkOut && isDateLessOrEquals(props.checkIn, props.date) &&
      isDateLessOrEquals(props.date, props.checkOut) ?
      isHighlighted.value = true : isHighlighted.value = false
  }
}

const createAllowedCheckoutDays = (date: Date) => {
  allowedCheckoutDays.value = [];
  props.options.allowedRanges.forEach((i) =>
    allowedCheckoutDays.value.push(addDays(date, i))
  )
  allowedCheckoutDays.value.sort((a, b) => a - b);
}

const disableNextDays = () => {
  if (props.nextDisabledDate !== Infinity && !isDateLessOrEquals(props.date, props.nextDisabledDate)) {
    isDisabled.value = true;
  }
  else if (props.checkIn && isDateLessOrEquals(props.date, props.checkIn)) {
    isDisabled.value = true;
  }
  if (props.checkIn && props.options.minNights == 0 && compareDay(props.date, props.checkIn) == 0) {
    isDisabled.value = false;
  }
  if (props.checkIn && props.options.enableCheckout && isDateLessOrEquals(props.checkIn, props.date)) {
    isDisabled.value = false
  }
  else {
    return
  }
}

// Watchers
watch(() => props.hoveringDate, (date) => {
  if (props.checkIn !== null && props.checkOut == null && isDisabled.value == false) {
    props.checkIn && props.hoveringDate && isDateLessOrEquals(props.checkIn, props.date) &&
      isDateLessOrEquals(props.date, props.hoveringDate) ?
      isHighlighted.value = true : isHighlighted.value = false
  }
  if (props.checkIn !== null && props.checkOut == null && allowedCheckoutDays.value.length !== 0) {
    props.checkIn && props.hoveringDate && createAllowedCheckoutDays(props.checkIn)
  }
})

watch(() => props.activeMonthIndex, (index) => {
  checkIfDisabled()
  checkIfHighlighted()
  if (props.checkIn !== null && props.checkOut !== null) {
    props.checkIn && props.checkOut && isDateLessOrEquals(props.checkIn, props.date) &&
      isDateLessOrEquals(props.date, props.checkOut) ?
      isHighlighted.value = true : isHighlighted.value = false
  } else if (props.checkIn !== null && props.checkOut == null) {
    disableNextDays()
  } else {
    return
  }
})

watch(() => props.nextDisabledDate, () => {
  disableNextDays();
})

watch(() => props.checkIn, (date: Date) => {
  props.checkIn && createAllowedCheckoutDays(date);
})

// Lifecycle
onBeforeMount(() => {
  props.checkIn && checkIfDisabled()
  props.checkIn && checkIfHighlighted()
})
</script>
